import { useState } from 'react';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs/operators';
import { modal, ModalLayout, ModalTemplate } from '@ardoq/modal';
import { TextInput } from '@ardoq/forms';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import {
  cloneConfiguration,
  saveConfigurationsFailure,
  saveConfigurationsSuccess,
} from 'integrations/common/streams/transferConfigs/actions';
import { SavedTransferConfig } from '@ardoq/api-types/integrations';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';

type DuplicateTransferConfigModalProps = {
  onSubmit: VoidFunction;
  onCancel: VoidFunction;
  config: SavedTransferConfig;
  integrationId: IntegrationId;
  i18nLocale: I18nLocale;
};

const DuplicateTransferConfigModalComponent = ({
  onSubmit,
  onCancel,
  config,
  integrationId,
  i18nLocale,
}: DuplicateTransferConfigModalProps) => {
  const copySuffix = t(
    i18nLocale,
    'integrations:duplicateTransferConfigModal.copySuffix',
    'copy'
  );
  const [configName, setConfigName] = useState(
    `${config.name} (${copySuffix})`
  );
  const [isCopying, setIsCopying] = useState(false);

  const handleCopyConfig = async () => {
    setIsCopying(true);
    const action = await dispatchActionAndWaitForResponse(
      cloneConfiguration({
        integrationId,
        name: configName.trim(),
        config,
      }),
      saveConfigurationsSuccess,
      saveConfigurationsFailure
    );
    setIsCopying(false);

    if (action.type === saveConfigurationsSuccess.type) {
      onSubmit();
    }
  };

  return (
    <ModalTemplate
      headerText={t(
        i18nLocale,
        'integrations:duplicateTransferConfigModal.headerText',
        'Duplicate configuration?'
      )}
      primaryButtonText={t(
        i18nLocale,
        'integrations:duplicateTransferConfigModal.primaryButtonText',
        'Duplicate configuration'
      )}
      secondaryButtonText={t(
        i18nLocale,
        'integrations:duplicateTransferConfigModal.secondaryButtonText',
        'Cancel'
      )}
      isPrimaryButtonDisabled={!configName.trim()}
      onPrimaryButtonClick={handleCopyConfig}
      onSecondaryButtonClick={onCancel}
      isInProgress={isCopying}
    >
      <ModalLayout>
        <TextInput
          label={t(
            i18nLocale,
            'integrations:duplicateTransferConfigModal.nameLabel',
            'Name of configuration'
          )}
          onValueChange={setConfigName}
          value={configName}
          onKeyDown={({ key }) => {
            if (key === 'Enter') {
              handleCopyConfig();
            }
          }}
        ></TextInput>
      </ModalLayout>
    </ModalTemplate>
  );
};

const viewModel$ = i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })));

const DuplicateTransferConfigModal = connect(
  DuplicateTransferConfigModalComponent,
  viewModel$
);

export const startDuplicateTransferConfigModal = (
  integrationId: IntegrationId,
  config: SavedTransferConfig
) =>
  modal<boolean>(
    resolve => (
      <DuplicateTransferConfigModal
        onSubmit={() => resolve(true)}
        onCancel={() => resolve(false)}
        config={config}
        integrationId={integrationId}
      />
    ),
    {
      autoFocus: true,
      shouldCloseOnEscapeKey: true,
      shouldCloseOnBackdropClick: false,
    }
  );
